import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
    CalculateDiscountsAndMultipliersService,
    CompanyWorker,
    Contractor,
    DiscountsAndMultipliersService,
    DocumentBuiltinFlagDict,
    DocumentNumberingService,
    DocumentStatus,
    DocumentStatusSystem,
    SalesDocumentType,
    PersonsService,
    PriceVersion,
    SalesDocument,
    SalesDocumentPosition,
    TaxRate,
    TaxRatesService,
} from '@icc/b2b/shared';
import { ConfigService, DynamicElementsService } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { isUndefined } from '@icc/shared/helpers/b2b';
import { DialogService, InputComponent } from '@icc/ui';
import { TranslateService } from '@ngx-translate/core';
import { DocumentStatusService } from 'libs/b2b/shared/src/lib/services/document-status.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { ClientDialogComponent } from '../client-dialog/client-dialog.component';
import { ClientPreviewDialogComponent } from '../client-preview-dialog/client-preview-dialog.component';
import { CompanyService } from '../company.service';
import { ChangedFlagEvent } from '../document-flags/document-flags.component';
import { DocumentStatusInfoDialogComponent } from '../document-status-info-dialog/document-status-info-dialog.component';
import { OfferValidityService } from '../offer-validity/offer-validity.service';
import { SalesDocumentPersonsService } from '../sales-document-persons.service';
import { SalesDocumentService } from '../sales-document.service';
import { SellerDialogComponent } from '../seller-dialog/seller-dialog.component';
import { DocumentTaxDialogComponent } from '../document-tax-dialog/document-tax-dialog.component';
import { DocumentHistoryDialogComponent } from '../document-history-dialog/document-history-dialog.component';
import { DocumentManagerService } from '../document-manager.service';
import { Subscription } from 'rxjs';
import { SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'icc-sales-document-details',
    templateUrl: './sales-document-details.component.html',
    styleUrls: ['./sales-document-details.component.scss'],
})
export class SalesDocumentDetailsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() document: SalesDocument;
    @Input() salesDocument: SalesDocument;
    @Input() documentList: SalesDocument[];
    @Input() positions: SalesDocumentPosition[];
    @Input() priceVersion: PriceVersion;
    @Input() groupingSchemaId: number;
    @Input() sortingMode: string;
    @Input() hasAccessToEditCode = false;
    @Input() hasAccessToEditRequestedDeadline = false;
    @Output() sellerChange = new EventEmitter();
    @Output() documentChange = new EventEmitter();
    @Output() invalidDate = new EventEmitter();

    statusColorHex = 'ffffff';
    editDocumentName = false;
    editDocumentCode = false;
    editRequestedDeadline = false;

    get customCode() {
        return (
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(null, SalesDocumentType.Quotation)
            ]?.includes('K_CUSTOM') && this.document?.salesDocumentTypeId === SalesDocumentType.Quotation
        );
    }

    documentCodeEditing = false;
    documentCodeEditingBlock = false;

    taxRateId: number | null = null;
    taxRates: TaxRate[] = [];

    additionalDiscount = 0;
    canQuotesSellerChange = false;
    salesDocumentSellerCompanyLicense = false;
    canEditClient = false;
    canEditSeller = false;

    workers: CompanyWorker[] = [];
    departments: Record<number, string> = {};

    statusSystem: DocumentStatusSystem | null = null;
    documentStatuses: DocumentStatus[] = [];
    documentStatusMap: Record<number, DocumentStatus> = {};
    singleBuyFlags = true;

    showValidityInfo = false;
    showDeadlineInfo = false;
    hasDeclaredDeadline = false;
    invalidOffer = false;
    visibleValidDate;
    showOfferValidityWarning;
    canIgnorePriceVersionValidity = false;

    estimatedDeadlineText: string;
    estimatedDeadlineInfo: SafeHtml;

    @ViewChild('documentCodeInput', { static: false }) documentCodeInput: InputComponent;
    @ViewChild('documentNameInput', { static: false }) documentNameInput: InputComponent;
    @ViewChild('requestedDeadlineInput', { static: false }) requestedDeadlineInput: InputComponent;

    private subscriptions: Subscription[] = [];

    // eslint-disable-next-line max-params
    constructor(
        private configService: ConfigService,
        private salesDocumentService: SalesDocumentService,
        private documentManagerService: DocumentManagerService,
        private salesDocumentPersonsService: SalesDocumentPersonsService,
        private personsService: PersonsService,
        private taxRateService: TaxRatesService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private permissionsService: NgxPermissionsService,
        private dialogService: DialogService,
        private companyService: CompanyService,
        private router: Router,
        private offerValidityService: OfferValidityService,
        private dynamicElementsService: DynamicElementsService,
        private discountsAndMultipliersService: DiscountsAndMultipliersService,
    ) {
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(async () => {
                this.canQuotesSellerChange = await this.permissionsService.hasPermission([
                    'quotes-seller-change',
                ]);
                this.checkCanEditSeller();
            }),
        );
    }

    async ngOnInit() {
        this.loadWorkers();
        this.loadDepartments();
        this.loadTaxRates();
        this.loadStatus();
        this.handleStatusColorHex();
        this.canQuotesSellerChange = await this.permissionsService.hasPermission([
            'quotes-seller-change',
        ]);
        this.setDiscount();
        await this.dynamicElementsService.loaded$.toPromise();
        this.estimatedDeadlineText =  this.dynamicElementsService.getElementContentBySlug('estimated-deadline-text', true);
        this.estimatedDeadlineInfo = this.dynamicElementsService.getElementHtmlContentBySlug('estimated-deadline-info');
    }

    async ngOnChanges() {
        if (this.document) {
            this.setTaxRate();
        }
        this.setCanEditClient();
        this.loadStatus();
        this.handleStatusColorHex();
        this.handleValidityDate();
        this.checkCanEditSeller();
        this.setDiscount();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    async checkCanEditSeller() {
        this.canEditSeller =
            ((await this.permissionsService.hasPermission(['purchase-resource-all-orders-list'])) ||
                (await this.permissionsService.hasPermission(['purchase-all-orders-list'])));
    }

    changeActiveDocument($event) {
        this.loadStatus();
        this.handleStatusColorHex();
        this.handleValidityDate();
        this.documentChange.next(this.documentList.find((el) => el.uuid === $event));
    }

    get side() {
        if (
            this.documentList?.length > 1 ||
            Number(this.document?.buyerCompanyId) ===
                Number(this.configService?.user?.user?.ActiveCompany) ||
            Number(this.documentList?.[0]?.buyerCompanyId) ===
                Number(this.configService?.user?.user?.ActiveCompany)
        ) {
            return 'buyer';
        }
        return 'seller';
    }

    async changeCustomCode(customCode: string) {
        await this.salesDocumentService.setDocumentCustomCode(this.document, customCode);
    }

    @HostListener('document:click', [])
    onDocumentClick() {
        if (this.documentCodeEditing && !this.documentCodeEditingBlock) {
            this.documentCodeEditing = false;
        } else {
            this.documentCodeEditingBlock = false;
        }
    }

    loadTaxRates() {
        this.taxRates = this.taxRateService.taxRates;
    }

    private setTaxRate() {
        this.taxRateId = this.document?.defaultTaxRateId ?? this.positions[0]?.priceTaxRateId;
    }

    setDiscount() {
        this.additionalDiscount = this.document?.priceAdditionalDiscount ?? 0;
    }

    async changeAdditionalDiscount(discount) {
        const offerDiscounts = this.discountsAndMultipliersService.offerDiscounts;
        const canExceedMaxDiscount = await this.permissionsService.hasPermission(['not-applicable-max-discount']);
        if (offerDiscounts?.maxDiscount !== null && Number(discount) > offerDiscounts?.maxDiscount) {
            if (canExceedMaxDiscount) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat dodatkowy'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Maksymalny rabat dodatkowy wynosi {maxDiscount}. Przekroczono rabat o {exceededDiscount}',
                        {
                            maxDiscount: CalculateDiscountsAndMultipliersService.formatDiscount(
                                offerDiscounts.maxDiscount,
                                'percent',
                            ),
                            exceededDiscount: CalculateDiscountsAndMultipliersService.formatDiscount(Number(discount) - offerDiscounts.maxDiscount, 'percent'),
                        }
                    ),
                    severity: 'warn',
                });
                this.salesDocumentService.changeAdditionalDiscount(this.document, Number(discount));
            } else {
                setTimeout(() => {
                    this.additionalDiscount = offerDiscounts.maxDiscount;
                    this.salesDocumentService.changeAdditionalDiscount(this.document, Number(offerDiscounts.maxDiscount));
                });
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat dodatkowy'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Maksymalny rabat dodatkowy wynosi {maxDiscount}. Ustawiono maksymalną wartość',
                        { maxDiscount: CalculateDiscountsAndMultipliersService.formatDiscount(
                            offerDiscounts.maxDiscount,
                            'percent',
                        ) }
                    ),
                    severity: 'warn',
                });
            }
        } else {
            this.salesDocumentService.changeAdditionalDiscount(this.document, Number(discount));
        }
    }

    async openClientPreviewDialog(isSeller = false) {
        let client;
        if (isSeller) {
            client = this.document?.seller || this.documentList?.[0]?.seller;
        } else {
            client = this.document?.client || this.documentList?.[0]?.client;
        }
        this.dialogService
            .open(ClientPreviewDialogComponent, {
                data: {
                    client,
                    isSeller,
                },
            })
            .afterClosed();
    }

    async loadDepartments() {
        await this.personsService.loaded;
        this.departments = this.personsService.getDepartments();
    }

    setCanEditClient() {
        const activeCompanyId = this.configService.user.user.ActiveCompany;
        if (this.document) {
            this.canEditClient =
                (Number(this.document?.sellerCompanyId) === Number(activeCompanyId) ||
                    Number(this.document?.buyerCompanyId) === Number(activeCompanyId)) &&
                !this.document?.isConfirmed &&
                !this.document?.isCanceled &&
                !this.document?.isOrderPlaced &&
                this.document?.salesDocumentTypeId !== SalesDocumentType.Order &&
                this.document?.salesDocumentTypeId !== SalesDocumentType.Inquiry;
        } else if (this.documentList) {
            this.canEditClient = this.documentList.every(
                (document) =>
                    (Number(document?.sellerCompanyId) === Number(activeCompanyId) ||
                        Number(document?.buyerCompanyId) === Number(activeCompanyId)) &&
                    !document?.isConfirmed &&
                    !document?.isCanceled &&
                    !document?.isOrderPlaced &&
                    document?.salesDocumentTypeId !== SalesDocumentType.Order &&
                    document?.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                    document?.salesDocumentTypeId !== SalesDocumentType.ServiceOrder
            );
        }
    }

    openClientDialog(existing = false) {
        this.dialogService
            .open(ClientDialogComponent, {
                data: {
                    client: (this.document || this.documentList?.[0])?.client,
                    resourceId: (this.document || this.documentList?.[0])?.buyerResourceId,
                    userId: (this.document || this.documentList?.[0])?.buyerUserId,
                    salesDocumentSellerCompanyLicense: this.salesDocumentSellerCompanyLicense,
                    existing,
                    isServiceOrder: (this.document || this.documentList?.[0])?.salesDocumentTypeId === SalesDocumentType.ServiceOrder,
                },
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    if (data?.id) {
                        this.salesDocumentPersonsService.changeClient(
                            this.document || this.documentList?.[0],
                            data
                        );
                    } else {
                        this.salesDocumentPersonsService.setNewClient(
                            this.document || this.documentList?.[0],
                            data
                        );
                    }
                }
            });
    }

    openTaxDialog() {
        this.dialogService
            .open(DocumentTaxDialogComponent, {
                data: {
                    document: this.document,
                    taxRateId: this.taxRateId,
                    taxRates: this.taxRates,
                },
            });
    }

    get author() {
        return this.salesDocumentPersonsService.getAuthor(this.document ?? this.documentList?.[0]);
    }

    get seller() {
        return (this.document ?? this.documentList?.[0])?.seller;
    }

    get sellerCompany() {
        return Number(this.configService.user.user.ActiveCompany) ===
            Number(this.document?.sellerCompanyId)
            ? this.configService.user.company
            : this.configService.user.accountManagerCompany;
    }

    async loadWorkers() {
        await this.personsService.loaded;
        this.workers = this.personsService.getWorkers();
    }

    async openSellerDialog() {
        const buyDocuments = await this.documentManagerService.getBuyDocuments(
            (this.salesDocument ?? this.document ?? this.documentList?.[0])?.uuid
        );
        this.dialogService
            .open(SellerDialogComponent, {
                data: {
                    resourceId: this.side === 'seller'
                        ? (this.salesDocument ?? this.document ?? this.documentList?.[0])
                            ?.sellerResourceId
                        : (this.document ?? buyDocuments?.[0])?.buyerResourceId,
                    deliveryAddressId: buyDocuments?.[0]?.client?.deliveryId,
                    workers: this.workers,
                    seller: this.configService.user.company,
                    sellerWorker:
                        this.side === 'seller'
                            ? this.personsService.getSeller(this.document)
                            : this.personsService.getWorkerByUserId(
                                  (this.document ?? this.documentList?.[0])?.buyerUserId
                              ),
                    resources: this.personsService.getResources(),
                    side: this.side,
                    canQuotesSellerChange: this.canQuotesSellerChange,
                },
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    if (data?.sellerWorker) {
                        if (this.side === 'seller') {
                            this.changeSeller(
                                data.sellerWorker,
                                data.resourceId,
                                data.deliveryAddressId,
                                data.deliveryAddress
                            );
                        } else {
                            this.changeBuyer(
                                data.sellerWorker,
                                data.resourceId,
                                data.deliveryAddressId,
                                data.deliveryAddress
                            );
                        }
                    }
                }
            });
    }


    canChangeSeller() {
        const sellerWorker = this.side === 'seller'
            ? this.personsService.getSeller(this.document)
            : this.personsService.getWorkerByUserId(
                (this.document ?? this.documentList?.[0])?.buyerUserId
            );
        return sellerWorker?.person?.user?.id === this.configService.user.user.id || this.canQuotesSellerChange || this.side === 'buyer';
    }

    async changeSeller(
        sellerWorker: CompanyWorker,
        resourceId: number,
        deliveryAddressId: number,
        deliveryAddress
    ) {
        await this.salesDocumentPersonsService.changeSeller(
            this.document,
            sellerWorker,
            resourceId,
            deliveryAddressId,
            deliveryAddress
        );

        if (
            !(await this.salesDocumentService.hasAccessToQuotationOrInquiry(
                this.salesDocument ?? this.document
            ))
        ) {
            this.router.navigate(['trade', 'offer', 'list', 'offers']);
        }
        this.sellerChange.emit();
    }

    async changeBuyer(
        sellerWorker: CompanyWorker,
        resourceId: number,
        deliveryAddressId: number,
        deliveryAddress
    ) {
        if (this.document) {
            await this.salesDocumentPersonsService.changeBuyer(
                this.document,
                sellerWorker,
                resourceId,
                deliveryAddressId,
                deliveryAddress
            );
        } else {
            for (let i = 0; i < this.documentList.length; i++) {
                await this.salesDocumentPersonsService.changeBuyer(
                    this.documentList[i],
                    sellerWorker,
                    resourceId,
                    deliveryAddressId,
                    deliveryAddress
                );
            }
        }
    }

    loadStatus() {
        this.statusSystem = this.configService.user.statusSystems[
            (this.document ?? this.documentList?.[0])?.salesDocumentTypeId
        ];
        this.documentStatuses = DocumentStatusService.getAvailableStatuses(
            this.document ?? this.documentList?.[0],
            this.configService.user.statusSystems,
            this.side
        );
        const allDocumentStatuses =
            this.configService.user.statusSystems[
                (this.document ?? this.documentList?.[0])?.salesDocumentTypeId
            ]?.statuses ?? [];
        this.documentStatusMap = {};
        allDocumentStatuses.forEach((status) => {
            this.documentStatusMap[status.id] = status;
        });

        if (this.document) {
            this.singleBuyFlags = true;
        } else if (this.statusSystem) {
            let matching = true;
            const firstFlag: Record<string, boolean> = {};
            for (const document of this.documentList) {
                let matchingInDocument = true;
                [1, 2].forEach((group) => {
                    const groupFlags = this.statusSystem.system.flagGroups[group];

                    groupFlags.forEach((groupFlag) => {
                        if (DocumentBuiltinFlagDict[groupFlag.code]) {
                            if (isUndefined(firstFlag[groupFlag.code])) {
                                firstFlag[groupFlag.code] = document[groupFlag.code];
                            }
                            if (
                                Boolean(document[groupFlag.code]) !==
                                Boolean(firstFlag[groupFlag.code])
                            ) {
                                matchingInDocument = false;
                            }
                        } else {
                            if (isUndefined(firstFlag[groupFlag.code])) {
                                firstFlag[groupFlag.code] = document.flags?.[groupFlag.code];
                            }
                            if (
                                Boolean(document.flags?.[groupFlag.code]) !==
                                Boolean(firstFlag[groupFlag.code])
                            ) {
                                matchingInDocument = false;
                            }
                        }
                    });
                });
                if (!matchingInDocument) {
                    matching = false;
                }
            }
            this.singleBuyFlags = matching;
        }
    }

    joinNumber(documentNumber: SalesDocument['documentNumber']) {
        return documentNumber?.join('') || '';
    }

    get status() {
        return this.documentStatuses.find(
            (el) => el.id === Number((this.document || this.documentList?.[0])?.statusId)
        );
    }

    get canChangeStatus() {
        return (
            (this.statusSystem?.system?.canBuyerChangeStatus && this.side === 'buyer') ||
            (this.statusSystem?.system?.canSellerChangeStatus && this.side === 'seller')
        );
    }

    get showStatus() {
        return this.statusSystem?.system?.showStatusesInB2b;
    }

    get showFlags() {
        return this.statusSystem?.system?.showFlagsInB2b &&
            this.statusSystem.system.flagGroup2Visibility !== 'hidden' &&
            this.statusSystem.system.flagGroup1Visibility !== 'hidden';
    }

    set showFlags(showFlags: boolean) {
        this.statusSystem.system.showFlagsInB2b = showFlags;
    }

    get isStatusInfoVisible() {
        return this.statusSystem?.system?.showStatusInfo;
    }

    changeStatus(statusId: number) {
        this.salesDocumentService.setStatus(this.document || this.documentList?.[0], statusId);
        this.handleStatusColorHex();
    }

    changeTitle(title: string) {
        this.salesDocumentService.setTitle(this.document, title);
    }

    changeDocumentCode(documentCode: string) {
        this.salesDocumentService.changeDocumentCode(this.document, documentCode);
    }

    changeRequestedDeadline(requestedDeadline: Date) {
        this.salesDocumentService.changeRequestedDeadline(this.document, requestedDeadline);
    }

    handleStatusColorHex() {
        if (this.status?.colorRgbHex) {
            this.statusColorHex = this.status.colorRgbHex;
        }
    }

    changeFlag(event: ChangedFlagEvent) {
        this.salesDocumentService.setFlag(
            this.document || this.documentList?.[0],
            event.flag,
            event.value,
            event.builtin
        );
    }

    showStatusInfo() {
        this.dialogService
            .open(DocumentStatusInfoDialogComponent, {
                data: {
                    document: this.document || this.documentList?.[0],
                    statusSystem: this.statusSystem,
                },
            })
            .afterClosed();
    }

    handleValidityDate() {
        if (this.document) {
            this.handleSingleDocumentValidity();
        } else if (this.documentList?.length) {
            this.handleMultipleDocumentValidity();
        }
    }

    private handleSingleDocumentValidity() {
        const {
            showValidityInfo,
            showOfferValidityWarning,
            invalidOffer,
            visibleValidDate,
        } = this.offerValidityService.getValidDate(this.document);
        this.showValidityInfo = showValidityInfo;
        this.showOfferValidityWarning = showOfferValidityWarning;
        this.invalidOffer = invalidOffer;
        this.visibleValidDate = visibleValidDate;
        this.invalidDate.emit(invalidOffer);
        this.canIgnorePriceVersionValidity = this.configService.user.user.ActiveCompanyLicenseType === 'license' &&
            this.priceVersion?.visibleForEmployee &&
            new Date() < this.document?.validityDate &&
            !this.document?.ignorePriceVersionValidity &&
            invalidOffer;
        this.handleSingleDocumentDeadline();
    }

    private handleSingleDocumentDeadline() {
        this.showDeadlineInfo = !this.showValidityInfo &&
            (this.document?.salesDocumentTypeId === SalesDocumentType.Order ||
                this.document?.salesDocumentTypeId === SalesDocumentType.ServiceOrder) &&
            !this.document?.isAnnexed &&
            !this.document?.isCanceled &&
            (this.side === 'buyer' ||
                this.configService.user.user.ActiveCompanyLicenseType === 'license');
        this.hasDeclaredDeadline = this.document?.declaredDeadline != null;
    }

    private handleMultipleDocumentValidity() {
        let commonShowValidityInfo = false;
        let commonShowOfferValidityWarning = false;
        let commonInvalidOffer = false;
        let commonVisibleValidDate = null;
        let commonShowDeadlineInfo = false;
        let hasDeclaredDeadline = true;
        for (const document of this.documentList) {
            const {
                showValidityInfo,
                showOfferValidityWarning,
                invalidOffer,
                visibleValidDate,
            } = this.offerValidityService.getValidDate(document);
            commonShowValidityInfo = commonShowValidityInfo || showValidityInfo;
            commonShowOfferValidityWarning =
                commonShowOfferValidityWarning || showOfferValidityWarning;
            commonInvalidOffer = commonInvalidOffer || invalidOffer;
            if (!commonVisibleValidDate || visibleValidDate < commonVisibleValidDate) {
                commonVisibleValidDate = visibleValidDate;
            }
            commonShowDeadlineInfo = commonShowDeadlineInfo || (
                !showValidityInfo &&
                (document?.salesDocumentTypeId === SalesDocumentType.Order ||
                document?.salesDocumentTypeId === SalesDocumentType.ServiceOrder) &&
                !document?.isAnnexed &&
                !document?.isCanceled
            );
            hasDeclaredDeadline = hasDeclaredDeadline && document?.declaredDeadline != null;
        }
        this.showValidityInfo = commonShowValidityInfo;
        this.showOfferValidityWarning = commonShowOfferValidityWarning;
        this.invalidOffer = commonInvalidOffer;
        this.visibleValidDate = commonVisibleValidDate;
        this.showDeadlineInfo = commonShowDeadlineInfo &&
            (this.side === 'buyer' ||
                this.configService.user.user.ActiveCompanyLicenseType === 'license');
        this.hasDeclaredDeadline = hasDeclaredDeadline;
        this.invalidDate.emit(commonInvalidOffer);
    }

    ignorePriceVersionValidity() {
        const title = this.translateService.instant(
            'SALES_DOCUMENT|Zezwolenie na edycję oferty'
        );
        const body = this.translateService.instant(
            'SALES_DOCUMENT|Wersja cenowa przypisana do dokumentu jest po terminie ważności. Czy na pewno chcesz zezwolić na edycję tego dokumentu?'
        );
        this.toastService.add({
            title,
            body,
            severity: 'question',
            sticky: true,
            buttons: [
                {
                    title: this.translateService.instant('INTERFACE|Tak'),
                    onClick: () => this.salesDocumentService.ignorePriceVersionValidity(this.document),
                },
                {
                    title: this.translateService.instant('INTERFACE|Nie'),
                    onClick: () => null,
                },
            ],
        });
    }

    getOldest(property: keyof SalesDocument) {
        let oldest = this.documentList[0]?.[property];
        for (const document of this.documentList) {
            if (document[property] < oldest) {
                oldest = document[property];
            }
        }
        return oldest;
    }

    getLatest(property: keyof SalesDocument) {
        let latest = this.documentList[0]?.[property];
        for (const document of this.documentList) {
            if (document[property] > latest) {
                latest = document[property];
            }
        }
        return latest;
    }

    clearDocumentName(document: SalesDocument) {
        this.editDocumentName = false;
        this.document.documentName = '';
    }

    openDocumentHistoryDialog() {
        this.dialogService
        .open(DocumentHistoryDialogComponent, {
            data: {
                document: this.document || this.documentList?.[0],
                salesDocument: this.salesDocument,
                documentStatusMap: this.documentStatusMap,
                side: this.side,
                groupingSchemaId: this.groupingSchemaId,
                sortingMode: this.sortingMode,
            },
        })
        .afterClosed();
    }

    focusInput(input: string) {
        if (input === 'documentCode') {
            setTimeout(() => {
                this.documentCodeInput?.focus();
            });
        } else if (input === 'documentName') {
            setTimeout(() => {
                this.documentNameInput?.focus();
            });
        } else if (input === 'requestedDeadline') {
            setTimeout(() => {
                this.requestedDeadlineInput?.focus();
            });
        }
    }

    isEmpty(value: any) {
        if (value === null || value === undefined) {
            return true;
        }
        if (typeof value === 'string' && value.trim() === '') {
            return true;
        }
        if (Array.isArray(value) && value.length === 0) {
            return true;
        }
        if (typeof value === 'object' && Object.keys(value).length === 0) {
            return true;
        }
        if (typeof value === 'boolean' && value === false) {
            return true;
        }
        return false;
    }
}
